import type { TagColorHex } from "@remi/enums/tag-color-hex";

const DARK_BACKGROUND_LUMINANCE_THRESHOLD = 0.15;

const TAG_COLOR_TRANSFORMS = {
  light: {
    border: { lightnessMultiplier: 0.96, chromaMultiplier: 1.12 },
    text: { lightnessMultiplier: 0.55, chromaMultiplier: 3.5 },
  },
  dark: {
    border: { lightnessMultiplier: 1.3, chromaMultiplier: 1.15 },
    text: { lightnessFixed: 0.9, chromaMultiplier: 2.0 },
  },
} as const;

export const TAG_COLOR_HEX_DARK = {
  FEEEEE: "#3d2828",
  FEF5E8: "#3d3428",
  FDF9EA: "#3d3a28",
  EEF7EE: "#283828",
  F0FBFF: "#28333d",
  EAF7F9: "#283438",
  F3EEF9: "#302838",
  FCEEFB: "#38283a",
  F7F7F7: "#2e2e2e",
  F0F0F0: "#303030",
} as const satisfies Record<TagColorHex, string>;

const darkHexByLightHex = new Map<string, string>(Object.entries(TAG_COLOR_HEX_DARK));

function borderColor(hex: string, transform: { lightnessMultiplier: number; chromaMultiplier: number }): string {
  return `oklch(from ${hex} calc(l * ${transform.lightnessMultiplier}) calc(c * ${transform.chromaMultiplier}) h)`;
}

function lightText(hex: string): string {
  const { lightnessMultiplier, chromaMultiplier } = TAG_COLOR_TRANSFORMS.light.text;
  return `oklch(from ${hex} calc(l * ${lightnessMultiplier}) calc(c * ${chromaMultiplier}) h)`;
}

function darkText(hex: string): string {
  const { lightnessFixed, chromaMultiplier } = TAG_COLOR_TRANSFORMS.dark.text;
  return `oklch(from ${hex} ${lightnessFixed} calc(c * ${chromaMultiplier}) h)`;
}

function relativeLuminance(hex: string): number {
  const normalizedHex = hex.startsWith("#") ? hex : `#${hex}`;
  const toLinear = (channel: number) => (channel <= 0.04045 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4);
  const r = toLinear(Number.parseInt(normalizedHex.slice(1, 3), 16) / 255);
  const g = toLinear(Number.parseInt(normalizedHex.slice(3, 5), 16) / 255);
  const b = toLinear(Number.parseInt(normalizedHex.slice(5, 7), 16) / 255);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

function lightColors(background: string) {
  return {
    background,
    border: borderColor(background, TAG_COLOR_TRANSFORMS.light.border),
    text: lightText(background),
  };
}

export function generateTagColors(hexCode: string, isDark = false) {
  const normalized = hexCode.startsWith("#") ? hexCode : `#${hexCode}`;
  if (!isDark) {
    return lightColors(normalized);
  }
  const key = normalized.slice(1).toUpperCase();
  const background = darkHexByLightHex.get(key) ?? normalized;
  // Off-palette free-form hexes keep light-mode rendering; only genuinely-dark backgrounds get light text.
  if (relativeLuminance(background) >= DARK_BACKGROUND_LUMINANCE_THRESHOLD) {
    return lightColors(background);
  }
  return {
    background,
    border: borderColor(background, TAG_COLOR_TRANSFORMS.dark.border),
    text: darkText(background),
  };
}
